import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import type { AttributeType, AttributeValidation } from '@messe/shared'
import { Button } from '../components/ui/button.js'
import { Dialog } from '../components/ui/dialog.js'
import { CheckboxField, SelectField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { format, t } from '../i18n/index.js'
import { cn } from '../lib/cn.js'
import {
  createAttribute,
  currentVersion,
  deactivateAttribute,
  hasUnpublishedChanges,
  listAttributes,
  listSchemaVersions,
  publishSchema,
  reorderAttributes,
  updateAttribute,
  type AttributeView,
} from '../lib/attributes.js'
import { listValueLists } from '../lib/value-lists.js'

const TYPES: AttributeType[] = [
  'text',
  'longtext',
  'number',
  'decimal',
  'integer',
  'boolean',
  'date',
  'datetime',
  'select',
  'multiselect',
  'barcode',
  'photo',
  'user',
]

/** The types whose default value has an obvious single-control representation. */
const TYPES_WITH_DEFAULT: AttributeType[] = [
  'text',
  'longtext',
  'number',
  'decimal',
  'integer',
  'boolean',
  'date',
]

/**
 * A field being edited, including changes not yet sent to the server.
 *
 * Kept separate from the query data so the preview can show what the form will
 * look like *before* saving — which is the point of having a preview at all.
 */
interface Draft {
  id: string | null
  key: string
  label: string
  type: AttributeType
  required: boolean
  carryOver: boolean
  helpText: string
  group: string
  showInList: boolean
  valueListId: string | null
  validation: AttributeValidation
  defaultValue: unknown
  system: boolean
  auto: boolean
  active: boolean
}

/**
 * The rules object for a freshly chosen type.
 *
 * Every type needs one whose `type` matches the field's, or the API rejects the
 * definition. Switching a field from text to decimal therefore has to throw the
 * old rules away rather than carry a maxLength into a number.
 */
function defaultValidationFor(type: AttributeType): AttributeValidation {
  switch (type) {
    case 'decimal':
      return { type: 'decimal', precision: 12, scale: 2 }
    default:
      return { type }
  }
}

function emptyDraft(): Draft {
  return {
    id: null,
    key: '',
    label: '',
    type: 'text',
    required: false,
    carryOver: false,
    helpText: '',
    group: '',
    showInList: false,
    valueListId: null,
    validation: defaultValidationFor('text'),
    defaultValue: null,
    system: false,
    auto: false,
    active: true,
  }
}

function draftFrom(attribute: AttributeView): Draft {
  return {
    id: attribute.id,
    key: attribute.key,
    label: attribute.label,
    type: attribute.type,
    required: attribute.required,
    carryOver: attribute.carryOver,
    helpText: attribute.helpText ?? '',
    group: attribute.group ?? '',
    showInList: attribute.showInList,
    valueListId: attribute.valueListId,
    validation: attribute.validation,
    defaultValue: attribute.defaultValue,
    system: attribute.system,
    auto: attribute.auto,
    active: attribute.active,
  }
}

export function AttributesPage() {
  const strings = t().attributes
  const queryClient = useQueryClient()

  const [showInactive, setShowInactive] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [publishOpen, setPublishOpen] = useState(false)
  const [publishNotes, setPublishNotes] = useState('')

  const attributes = useQuery({
    queryKey: ['attributes', showInactive],
    queryFn: () => listAttributes(showInactive),
  })
  const valueLists = useQuery({ queryKey: ['value-lists'], queryFn: listValueLists })
  const versions = useQuery({ queryKey: ['schema-versions'], queryFn: listSchemaVersions })

  const fields = useMemo(
    () => [...(attributes.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [attributes.data],
  )

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['attributes'] })
    await queryClient.invalidateQueries({ queryKey: ['schema-versions'] })
  }

  const save = useMutation({
    mutationFn: async (input: Draft) => {
      if (input.id === null) {
        return createAttribute({
          key: input.key,
          label: input.label,
          type: input.type,
          required: input.required,
          carryOver: input.carryOver,
          helpText: input.helpText === '' ? null : input.helpText,
          group: input.group === '' ? null : input.group,
          sortOrder: fields.length,
          showInList: input.showInList,
          valueListId: input.valueListId,
          validation: input.validation,
          defaultValue: input.defaultValue,
        })
      }

      // A system field accepts only the five properties that cannot break sync,
      // duplicate detection or export. Sending more is rejected, so the payload
      // is narrowed here rather than hoping the form disabled every control.
      if (input.system) {
        return updateAttribute(input.id, {
          label: input.label,
          helpText: input.helpText === '' ? null : input.helpText,
          group: input.group === '' ? null : input.group,
          showInList: input.showInList,
        })
      }

      return updateAttribute(input.id, {
        label: input.label,
        type: input.type,
        required: input.required,
        carryOver: input.carryOver,
        helpText: input.helpText === '' ? null : input.helpText,
        group: input.group === '' ? null : input.group,
        showInList: input.showInList,
        valueListId: input.valueListId,
        validation: input.validation,
        defaultValue: input.defaultValue,
      })
    },
    onSuccess: async (_result, input) => {
      await refresh()
      setNotice(input.id === null ? strings.created : strings.saved)
      setProblem(null)
      setDraft(null)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const setActive = useMutation({
    mutationFn: async ({ attribute, active }: { attribute: AttributeView; active: boolean }) => {
      // Two different endpoints: reactivating is an ordinary patch, while
      // deactivating goes through DELETE because it is the same soft-delete the
      // API uses everywhere else.
      if (active) {
        await updateAttribute(attribute.id, { active: true })
      } else {
        await deactivateAttribute(attribute.id)
      }
    },
    onSuccess: async () => {
      await refresh()
      setDraft(null)
      setProblem(null)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const reorder = useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) => reorderAttributes(order),
    onSuccess: async () => {
      await refresh()
      setNotice(strings.orderSaved)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const publish = useMutation({
    mutationFn: (notes: string) => publishSchema(notes === '' ? undefined : notes),
    onSuccess: async (result) => {
      await refresh()
      setPublishOpen(false)
      setPublishNotes('')
      setNotice(
        result.created
          ? format(strings.published, { version: result.version })
          : strings.nothingToPublish,
      )
    },
    onError: (error: Error) => setProblem(error.message),
  })

  function move(from: number, to: number) {
    if (to < 0 || to >= fields.length || from === to) return

    const next = [...fields]
    const [moved] = next.splice(from, 1)
    if (!moved) return
    next.splice(to, 0, moved)

    reorder.mutate(next.map((field, index) => ({ id: field.id, sortOrder: index })))
  }

  const version = currentVersion(versions.data ?? [])
  const pending = hasUnpublishedChanges(attributes.data ?? [], versions.data ?? [])

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{strings.title}</h1>
          <p className="text-sm text-slate-600">{strings.subtitle}</p>
        </div>
        <Button onClick={() => setDraft(emptyDraft())}>{strings.newField}</Button>
      </header>

      {notice !== null && <Alert tone="success">{notice}</Alert>}
      {problem !== null && <Alert tone="danger">{problem}</Alert>}
      {attributes.isError && <Alert tone="danger">{strings.loadError}</Alert>}

      <PublishBar
        version={version}
        pending={pending}
        onPublish={() => setPublishOpen(true)}
        disabled={publish.isPending}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {format(strings.fieldCount, { count: fields.length })}
            </h2>
            <CheckboxField
              label={strings.showInactive}
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
          </div>

          {fields.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              {strings.noFields}
            </p>
          ) : (
            <ol className="space-y-2" data-testid="attribute-list">
              {fields.map((field, index) => (
                <li
                  key={field.id}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragEnd={() => setDragIndex(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault()
                    if (dragIndex !== null) move(dragIndex, index)
                    setDragIndex(null)
                  }}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border bg-white p-3',
                    draft?.id === field.id ? 'border-accent' : 'border-slate-200',
                    !field.active && 'opacity-60',
                    dragIndex === index && 'opacity-50',
                  )}
                >
                  <span className="cursor-grab select-none text-slate-400" aria-hidden="true">
                    ⠿
                  </span>

                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setDraft(draftFrom(field))}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{field.label}</span>
                      {field.system && <Badge>{strings.systemField}</Badge>}
                      {!field.active && <Badge tone="warning">{strings.inactive}</Badge>}
                    </span>
                    <span className="block text-sm text-slate-500">
                      {field.key} · {strings.types[field.type]}
                      {field.required && ' · ' + strings.required}
                    </span>
                  </button>

                  {/* Keyboard equivalents: dragging is unreachable without a
                      pointer, and the portal stays operable without one. */}
                  <button
                    type="button"
                    aria-label={strings.moveUp}
                    className="px-1 text-slate-500 disabled:opacity-30"
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(index, index - 1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={strings.moveDown}
                    className="px-1 text-slate-500 disabled:opacity-30"
                    disabled={index === fields.length - 1 || reorder.isPending}
                    onClick={() => move(index, index + 1)}
                  >
                    ↓
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="space-y-6">
          {draft === null ? (
            <FormPreview fields={fields} draft={null} />
          ) : (
            <>
              <FieldEditor
                draft={draft}
                valueLists={(valueLists.data ?? []).map((list) => ({
                  id: list.id,
                  name: list.name,
                }))}
                isSaving={save.isPending}
                onChange={setDraft}
                onCancel={() => setDraft(null)}
                onSave={() => save.mutate(draft)}
                onToggleActive={() => {
                  const attribute = fields.find((field) => field.id === draft.id)
                  if (!attribute) return
                  if (attribute.active && !window.confirm(strings.deactivateConfirm)) return
                  setActive.mutate({ attribute, active: !attribute.active })
                }}
              />
              <FormPreview fields={fields} draft={draft} />
            </>
          )}
        </section>
      </div>

      <Dialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title={strings.publish}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPublishOpen(false)}>
              {strings.cancel}
            </Button>
            <Button onClick={() => publish.mutate(publishNotes)} disabled={publish.isPending}>
              {publish.isPending ? strings.publishing : strings.publish}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Alert tone="warning">{strings.publishWarning}</Alert>
          <TextField
            label={strings.publishNotes}
            hint={strings.publishNotesHint}
            value={publishNotes}
            onChange={(event) => setPublishNotes(event.target.value)}
          />
        </div>
      </Dialog>
    </div>
  )
}

function PublishBar({
  version,
  pending,
  onPublish,
  disabled,
}: {
  version: { version: number; publishedAt: string | null } | null
  pending: boolean
  onPublish: () => void
  disabled: boolean
}) {
  const strings = t().attributes

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4"
      data-testid="publish-bar"
    >
      <div className="min-w-0">
        <p className="font-medium">
          {version === null
            ? strings.neverPublished
            : format(strings.version, { version: version.version })}
        </p>
        {version?.publishedAt != null && (
          <p className="text-sm text-slate-500">
            {format(strings.publishedAt, {
              date: new Date(version.publishedAt).toLocaleString('de-DE'),
            })}
          </p>
        )}
        {pending && (
          <p className="mt-2 text-warning text-sm font-medium">
            {strings.unpublished}
            <span className="block font-normal text-slate-500">{strings.unpublishedHint}</span>
          </p>
        )}
      </div>

      <Button onClick={onPublish} disabled={disabled}>
        {strings.publish}
      </Button>
    </div>
  )
}

function FieldEditor({
  draft,
  valueLists,
  isSaving,
  onChange,
  onCancel,
  onSave,
  onToggleActive,
}: {
  draft: Draft
  valueLists: { id: string; name: string }[]
  isSaving: boolean
  onChange: (draft: Draft) => void
  onCancel: () => void
  onSave: () => void
  onToggleActive: () => void
}) {
  const strings = t().attributes
  const isNew = draft.id === null
  const needsList = draft.type === 'select' || draft.type === 'multiselect'

  // A system field's key, type and required flag are frozen; everything else on
  // it stays editable. Locking the whole record instead would make a typo in a
  // label permanent.
  const locked = draft.system

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    onChange({ ...draft, [key]: value })
  }

  function setType(type: AttributeType) {
    onChange({
      ...draft,
      type,
      validation: defaultValidationFor(type),
      // The API refuses a list on anything but select, and refuses its absence
      // on those two. Clearing it here keeps the form from offering a state the
      // server will reject.
      valueListId: type === 'select' || type === 'multiselect' ? draft.valueListId : null,
      defaultValue: TYPES_WITH_DEFAULT.includes(type) ? draft.defaultValue : null,
    })
  }

  const saveable =
    draft.label.trim() !== '' &&
    (isNew ? /^[a-z][a-z0-9_]*$/.test(draft.key) : true) &&
    (!needsList || draft.valueListId !== null)

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{isNew ? strings.newField : draft.label}</h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {strings.cancel}
          </Button>
          <Button onClick={onSave} disabled={isSaving || !saveable}>
            {isSaving ? strings.saving : strings.save}
          </Button>
        </div>
      </div>

      {locked && <Alert tone="info">{strings.systemFieldHint}</Alert>}

      <TextField
        label={strings.key}
        hint={strings.keyHint}
        value={draft.key}
        // The key is the JSONB property name and the export column identifier.
        // Renaming it would orphan every value already captured under the old
        // one, so it is settled once, when the field is created.
        disabled={!isNew}
        onChange={(event) => set('key', event.target.value)}
      />

      <TextField
        label={strings.label}
        hint={strings.labelHint}
        value={draft.label}
        onChange={(event) => set('label', event.target.value)}
      />

      <SelectField
        label={strings.type}
        value={draft.type}
        disabled={locked}
        options={TYPES.map((type) => ({ value: type, label: strings.types[type] }))}
        onChange={(event) => setType(event.target.value as AttributeType)}
      />

      {needsList && (
        <SelectField
          label={strings.valueList}
          hint={strings.valueListHint}
          value={draft.valueListId ?? ''}
          disabled={locked}
          options={[
            { value: '', label: strings.noValueList },
            ...valueLists.map((list) => ({ value: list.id, label: list.name })),
          ]}
          onChange={(event) =>
            set('valueListId', event.target.value === '' ? null : event.target.value)
          }
        />
      )}

      <TextField
        label={strings.helpText}
        hint={strings.helpTextHint}
        value={draft.helpText}
        onChange={(event) => set('helpText', event.target.value)}
      />

      <TextField
        label={strings.group}
        hint={strings.groupHint}
        value={draft.group}
        onChange={(event) => set('group', event.target.value)}
      />

      <div className="space-y-2">
        <CheckboxField
          label={strings.required}
          checked={draft.required}
          disabled={locked}
          onChange={(event) => set('required', event.target.checked)}
        />
        <CheckboxField
          label={strings.carryOver}
          hint={strings.carryOverHint}
          checked={draft.carryOver}
          disabled={locked}
          onChange={(event) => set('carryOver', event.target.checked)}
        />
        <CheckboxField
          label={strings.showInList}
          checked={draft.showInList}
          onChange={(event) => set('showInList', event.target.checked)}
        />
      </div>

      {!locked && (
        <ValidationEditor
          validation={draft.validation}
          onChange={(validation) => set('validation', validation)}
        />
      )}

      {!locked && TYPES_WITH_DEFAULT.includes(draft.type) && (
        <DefaultValueEditor
          type={draft.type}
          value={draft.defaultValue}
          onChange={(value) => set('defaultValue', value)}
        />
      )}

      {!isNew && !draft.system && (
        <div className="border-t border-slate-200 pt-4">
          <Button variant={draft.active ? 'danger' : 'secondary'} onClick={onToggleActive}>
            {draft.active ? strings.deactivate : strings.reactivate}
          </Button>
        </div>
      )}
    </div>
  )
}

/** Reads a number input, treating an empty field as "no rule" rather than zero. */
function numberOrUndefined(raw: string): number | undefined {
  if (raw.trim() === '') return undefined
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

function ValidationEditor({
  validation,
  onChange,
}: {
  validation: AttributeValidation
  onChange: (validation: AttributeValidation) => void
}) {
  const strings = t().attributes

  // Each caller below patches a rule that belongs to the branch it is in, so
  // the discriminant is never the thing being changed. Typed as a partial of
  // the union rather than a bare record, which keeps the spread honest.
  function patch(changes: Partial<AttributeValidation>) {
    onChange({ ...validation, ...changes } as AttributeValidation)
  }

  const controls: React.ReactNode[] = []

  switch (validation.type) {
    case 'text':
    case 'longtext':
      controls.push(
        <TextField
          key="minLength"
          label={strings.minLength}
          type="number"
          min={0}
          value={validation.minLength ?? ''}
          onChange={(event) => patch({ minLength: numberOrUndefined(event.target.value) })}
        />,
        <TextField
          key="maxLength"
          label={strings.maxLength}
          type="number"
          min={1}
          value={validation.maxLength ?? ''}
          onChange={(event) => patch({ maxLength: numberOrUndefined(event.target.value) })}
        />,
      )
      if (validation.type === 'text') {
        controls.push(
          <TextField
            key="pattern"
            label={strings.pattern}
            value={validation.pattern ?? ''}
            onChange={(event) =>
              patch({ pattern: event.target.value === '' ? undefined : event.target.value })
            }
          />,
          <TextField
            key="patternMessage"
            label={strings.patternMessage}
            value={validation.patternMessage ?? ''}
            onChange={(event) =>
              patch({ patternMessage: event.target.value === '' ? undefined : event.target.value })
            }
          />,
        )
      }
      break

    case 'number':
    case 'integer':
    case 'decimal':
      controls.push(
        <TextField
          key="min"
          label={strings.min}
          type="number"
          value={validation.min ?? ''}
          onChange={(event) => patch({ min: numberOrUndefined(event.target.value) })}
        />,
        <TextField
          key="max"
          label={strings.max}
          type="number"
          value={validation.max ?? ''}
          onChange={(event) => patch({ max: numberOrUndefined(event.target.value) })}
        />,
      )
      if (validation.type === 'decimal') {
        controls.push(
          <TextField
            key="precision"
            label={strings.precision}
            type="number"
            min={1}
            max={38}
            value={validation.precision}
            onChange={(event) => patch({ precision: numberOrUndefined(event.target.value) ?? 12 })}
          />,
          <TextField
            key="scale"
            label={strings.scale}
            type="number"
            min={0}
            max={10}
            value={validation.scale}
            onChange={(event) => patch({ scale: numberOrUndefined(event.target.value) ?? 2 })}
          />,
        )
      }
      break

    case 'date':
    case 'datetime':
      controls.push(
        <TextField
          key="min"
          label={strings.min}
          type={validation.type === 'date' ? 'date' : 'datetime-local'}
          value={validation.min ?? ''}
          onChange={(event) =>
            patch({ min: event.target.value === '' ? undefined : event.target.value })
          }
        />,
        <TextField
          key="max"
          label={strings.max}
          type={validation.type === 'date' ? 'date' : 'datetime-local'}
          value={validation.max ?? ''}
          onChange={(event) =>
            patch({ max: event.target.value === '' ? undefined : event.target.value })
          }
        />,
      )
      break

    case 'multiselect':
      controls.push(
        <TextField
          key="minItems"
          label={strings.minItems}
          type="number"
          min={0}
          value={validation.minItems ?? ''}
          onChange={(event) => patch({ minItems: numberOrUndefined(event.target.value) })}
        />,
        <TextField
          key="maxItems"
          label={strings.maxItems}
          type="number"
          min={1}
          value={validation.maxItems ?? ''}
          onChange={(event) => patch({ maxItems: numberOrUndefined(event.target.value) })}
        />,
      )
      break

    case 'photo':
      controls.push(
        <TextField
          key="minCount"
          label={strings.minCount}
          type="number"
          min={0}
          max={50}
          value={validation.minCount ?? ''}
          onChange={(event) => patch({ minCount: numberOrUndefined(event.target.value) })}
        />,
        <TextField
          key="maxCount"
          label={strings.maxCount}
          type="number"
          min={1}
          max={50}
          value={validation.maxCount ?? ''}
          onChange={(event) => patch({ maxCount: numberOrUndefined(event.target.value) })}
        />,
      )
      break

    case 'barcode':
      controls.push(
        <TextField
          key="symbologies"
          label={strings.symbologies}
          hint={strings.symbologiesHint}
          value={(validation.symbologies ?? []).join(', ')}
          onChange={(event) => {
            const entries = event.target.value
              .split(',')
              .map((entry) => entry.trim())
              .filter((entry) => entry !== '')
            patch({ symbologies: entries.length === 0 ? undefined : entries })
          }}
        />,
        <TextField
          key="pattern"
          label={strings.pattern}
          value={validation.pattern ?? ''}
          onChange={(event) =>
            patch({ pattern: event.target.value === '' ? undefined : event.target.value })
          }
        />,
      )
      break

    // boolean, select and user carry no rules of their own.
    default:
      break
  }

  return (
    <div className="space-y-3 border-t border-slate-200 pt-4">
      <h3 className="text-sm font-semibold text-slate-700">{strings.rules}</h3>
      {controls.length === 0 ? (
        <p className="text-sm text-slate-500">{strings.noRules}</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">{controls}</div>
      )}
    </div>
  )
}

/** A default is a scalar by construction: only simple types offer the control. */
function defaultValueAsText(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

function DefaultValueEditor({
  type,
  value,
  onChange,
}: {
  type: AttributeType
  value: unknown
  onChange: (value: unknown) => void
}) {
  const strings = t().attributes

  if (type === 'boolean') {
    return (
      <CheckboxField
        label={strings.defaultValue}
        checked={value === true}
        onChange={(event) => onChange(event.target.checked ? true : null)}
      />
    )
  }

  const numeric = type === 'number' || type === 'decimal' || type === 'integer'

  return (
    <TextField
      label={strings.defaultValue}
      type={numeric ? 'number' : type === 'date' ? 'date' : 'text'}
      value={defaultValueAsText(value)}
      onChange={(event) => {
        const raw = event.target.value
        if (raw === '') {
          onChange(null)
          return
        }
        onChange(numeric ? (numberOrUndefined(raw) ?? null) : raw)
      }}
    />
  )
}

/**
 * The capture form as the app would build it.
 *
 * Rendered from the saved catalogue with the unsaved draft laid over it, so a
 * change is visible before it is committed. Grouping and order follow the same
 * rules the app uses: fields sorted by sortOrder, grouped by `group`, with
 * ungrouped fields first.
 *
 * Deliberately not interactive. Its job is to answer "will the form look right",
 * and a preview that accepts input invites the question of where that input
 * goes.
 */
function FormPreview({ fields, draft }: { fields: AttributeView[]; draft: Draft | null }) {
  const strings = t().attributes

  const effective = useMemo(() => {
    /**
     * The draft laid over the saved record. Built field by field rather than
     * spread, because a draft's id is null while it is new and spreading it
     * over a saved field would quietly erase that field's identity.
     */
    function applied(field: AttributeView): AttributeView {
      if (draft?.id !== field.id) return field

      return {
        ...field,
        label: draft.label,
        type: draft.type,
        required: draft.required,
        carryOver: draft.carryOver,
        helpText: draft.helpText === '' ? null : draft.helpText,
        group: draft.group === '' ? null : draft.group,
        showInList: draft.showInList,
        valueListId: draft.valueListId,
        validation: draft.validation,
        defaultValue: draft.defaultValue,
      }
    }

    const merged = fields.filter((field) => field.active).map(applied)

    // A field that does not exist yet still belongs in the preview — seeing
    // where it lands is the reason to look before saving.
    if (draft !== null && draft.id === null && draft.label.trim() !== '') {
      merged.push({
        id: 'draft',
        key: draft.key,
        label: draft.label,
        type: draft.type,
        system: false,
        auto: false,
        required: draft.required,
        active: true,
        carryOver: draft.carryOver,
        helpText: draft.helpText === '' ? null : draft.helpText,
        group: draft.group === '' ? null : draft.group,
        sortOrder: merged.length,
        showInList: draft.showInList,
        valueListId: draft.valueListId,
        valueListKey: null,
        validation: draft.validation,
        storage: 'jsonb',
        defaultValue: draft.defaultValue,
        createdAt: '',
        updatedAt: '',
      })
    }

    return merged
  }, [fields, draft])

  const groups = useMemo(() => {
    const byGroup = new Map<string, AttributeView[]>()
    for (const field of effective) {
      const key = field.group ?? ''
      byGroup.set(key, [...(byGroup.get(key) ?? []), field])
    }
    // Ungrouped first, then the rest in the order they first appear.
    return [...byGroup.entries()].sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : 0))
  }, [effective])

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div>
        <h2 className="text-lg font-semibold">{strings.preview}</h2>
        <p className="text-sm text-slate-500">{strings.previewHint}</p>
      </div>

      {effective.length === 0 ? (
        <p className="text-sm text-slate-500">{strings.previewEmpty}</p>
      ) : (
        <div
          className="mx-auto max-w-sm space-y-4 rounded-xl border border-slate-300 bg-white p-4"
          data-testid="form-preview"
        >
          {groups.map(([group, groupFields]) => (
            <section key={group || 'ungrouped'} className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {group === '' ? strings.previewUngrouped : group}
              </h3>
              {groupFields.map((field) => (
                <PreviewField key={field.id} field={field} />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function PreviewField({ field }: { field: AttributeView }) {
  const strings = t().attributes

  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">
        {field.label}
        {field.required && <span className="text-danger ml-1">*</span>}
      </p>

      {field.auto ? (
        <p className="rounded border border-dashed border-slate-300 px-2 py-1.5 text-sm italic text-slate-400">
          {strings.previewAuto}
        </p>
      ) : (
        <PreviewControl type={field.type} />
      )}

      {(field.helpText ?? '') !== '' && <p className="text-xs text-slate-500">{field.helpText}</p>}
    </div>
  )
}

function PreviewControl({ type }: { type: AttributeType }) {
  const strings = t().attributes
  const shell = 'rounded border border-slate-300 bg-slate-50 px-2 py-1.5 text-sm text-slate-400'

  switch (type) {
    case 'boolean':
      return (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span className="inline-block size-4 rounded border border-slate-300 bg-slate-50" />
          <span>{strings.types.boolean}</span>
        </div>
      )
    case 'photo':
      return (
        <div className="flex gap-2">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              className="flex size-14 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400"
            >
              {index === 0 ? '＋' : ''}
            </span>
          ))}
        </div>
      )
    case 'barcode':
      return <p className={shell}>{strings.previewScan}</p>
    case 'select':
    case 'multiselect':
    case 'user':
      return <p className={shell}>{strings.previewSelect}</p>
    case 'longtext':
      return <p className={cn(shell, 'h-16')} />
    default:
      return <p className={shell}>&nbsp;</p>
  }
}
